/**
 * Estimates of the «Caché» page, from what the last reload measured.
 *
 * The ONE formula, here and not in the backend, so a click on a switch or
 * a language box recalculates without a round trip. Everything is an
 * ESTIMATE and the page says so:
 *
 * - a report's cost per (variant × locale) is the last warm's seconds
 *   divided by the variants × locales it computed;
 * - with the option on it runs `variants_if_enabled` variants, off only
 *   the default one;
 * - a report whose SQL reads `{filter:__locale}` runs once per selected
 *   language, any other report once;
 * - the wall time is the seconds divided by the workers (the warm runs
 *   `workers` variants at a time).
 *
 * A report with no measurement is `null`, never 0: the totals leave it
 * out and count it in `unmeasured`.
 */

import type { CacheProject, CacheReport } from "./cache-api";

/** Seconds of computation for `report` with this option and languages. */
export function reportSeconds(
  report: CacheReport,
  enabled: boolean,
  languages: number,
): number | null {
  const last = report.last;
  if (
    last === null ||
    last.seconds === null ||
    !last.variants ||
    !last.locales
  ) {
    return null;
  }
  const perUnit = last.seconds / (last.variants * last.locales);
  const variants = enabled ? report.variants_if_enabled : 1;
  const locales = report.reads_locale ? languages : 1;
  return perUnit * variants * locales;
}

/** What turning the option on adds, in seconds of computation. */
export function extraSeconds(
  report: CacheReport,
  languages: number,
): number | null {
  const on = reportSeconds(report, true, languages);
  const off = reportSeconds(report, false, languages);
  return on === null || off === null ? null : on - off;
}

/** The option a row shows: a group's options follow any tab with it on. */
export function effectiveEnabled(
  report: CacheReport,
  project: CacheProject,
  overrides: Record<string, boolean>,
): boolean {
  const own = (r: CacheReport): boolean => overrides[r.key] ?? r.enabled;
  if (!report.is_group_options) return own(report);
  return project.reports.some(
    (r) => !r.is_group_options && r.group_key === report.group_key && own(r),
  );
}

export interface Totals {
  /** Seconds of computation of the measured reports. */
  seconds: number;
  /** Same, with every option off (defaults only). */
  defaultsSeconds: number;
  /** Reports left out because they have no measurement. */
  unmeasured: number;
}

export function projectTotals(
  project: CacheProject,
  overrides: Record<string, boolean>,
  languages: number,
): Totals {
  let seconds = 0;
  let defaultsSeconds = 0;
  let unmeasured = 0;
  for (const report of project.reports) {
    const now = reportSeconds(
      report,
      effectiveEnabled(report, project, overrides),
      languages,
    );
    const base = reportSeconds(report, false, languages);
    if (now === null || base === null) {
      unmeasured += 1;
      continue;
    }
    seconds += now;
    defaultsSeconds += base;
  }
  return { seconds, defaultsSeconds, unmeasured };
}

export function sumTotals(all: Totals[]): Totals {
  return all.reduce(
    (acc, t) => ({
      seconds: acc.seconds + t.seconds,
      defaultsSeconds: acc.defaultsSeconds + t.defaultsSeconds,
      unmeasured: acc.unmeasured + t.unmeasured,
    }),
    { seconds: 0, defaultsSeconds: 0, unmeasured: 0 },
  );
}

/** "< 1 s", "33 s", "6,6 min" in the reader's locale. */
export function formatDuration(seconds: number, locale: string): string {
  if (seconds < 1) return "< 1 s";
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const minutes = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(seconds / 60);
  return `${minutes} min`;
}
